import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutGrid, ClipboardList, BookOpen, Clapperboard, Newspaper, Layers,
  Bookmark, FileText, Search, User, ShieldCheck, LogOut, Menu,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import logoImg from '../../assets/logo.png';
import './Layout.css';

// ── UPDATE THESE DATES EACH YEAR ──────────────────────────────────────────────
const EXAM_DATES = {
  prelims: new Date('2027-05-23T09:30:00+05:30'),
  mains:   new Date('2027-08-20T09:00:00+05:30'),
};

function useCountdown(targetDate) {
  const [t, setT] = useState(null);
  useEffect(() => {
    const calc = () => {
      const diff = targetDate - new Date();
      if (diff <= 0) return setT({ days: 0, hours: 0, minutes: 0, passed: true });
      setT({
        days:    Math.floor(diff / 86400000),
        hours:   Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        passed:  false,
      });
    };
    calc();
    const id = setInterval(calc, 30000);
    return () => clearInterval(id);
  }, [targetDate]);
  return t;
}

/* Days left as a small chip; the full countdown shows in the tooltip */
function CountdownChip({ letter, label, date, tipProps }) {
  const t = useCountdown(date);
  if (!t) return null;
  const full = t.passed
    ? `${label} · done`
    : `${label} · ${t.days}d ${String(t.hours).padStart(2, '0')}h ${String(t.minutes).padStart(2, '0')}m`;
  return (
    <div className="rail-cd" role="img" aria-label={full} {...tipProps(full)}>
      <small>{letter}</small>
      <b>{t.passed ? '–' : t.days}</b>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tip, setTip] = useState(null);

  useEffect(() => {
    let can = document.querySelector('link[rel="canonical"]');
    if (!can) {
      can = document.createElement('link');
      can.rel = 'canonical';
      document.head.appendChild(can);
    }
    can.href = 'https://civilfox.app' + location.pathname;
  }, [location.pathname]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isAdmin = user?.role === 'admin';
  const path = location.pathname.replace(/\/$/, '');
  const isCanvas = ['/dashboard', '/prelims', '/mains', '/profile'].includes(path);   // one white canvas, edge to edge
  const isDark = path === '/pyq';                                         // night canvas for the book shelf
  const isBleed = isCanvas || isDark || path === '/news' || path === '/pyq-search';

  const NAV = [
    { to: '/dashboard',           Icon: LayoutGrid,    label: 'Dashboard' },
    { to: '/prelims',             Icon: ClipboardList, label: 'Study Prelims' },
    { to: '/mains',               Icon: BookOpen,      label: 'Study Mains' },
    { to: '/study-upsc-syllabus', Icon: Clapperboard,  label: 'Video Syllabus', external: true },
    { to: '/news',                Icon: Newspaper,     label: 'UPSC News' },
    { to: '/browse',              Icon: Layers,        label: 'Browse Cards' },
    { to: '/bookmarks',           Icon: Bookmark,      label: 'Bookmarks' },
    { to: '/pyq',                 Icon: FileText,      label: 'PYQ Papers' },
    { to: '/pyq-search',          Icon: Search,        label: 'PYQ Questions' },
    { to: '/profile',             Icon: User,          label: 'Profile' },
    ...(isAdmin ? [{ to: '/admin', Icon: ShieldCheck, label: 'Admin' }] : []),
  ];

  /* Tooltips are drawn outside the rail so its scroll area never clips them (desktop only) */
  const showTip = (text, el) => {
    if (window.innerWidth <= 768) return;
    const r = el.getBoundingClientRect();
    setTip({ text, y: r.top + r.height / 2 });
  };
  const tipProps = text => ({
    onMouseEnter: e => showTip(text, e.currentTarget),
    onFocus: e => showTip(text, e.currentTarget),
    onMouseLeave: () => setTip(null),
    onBlur: () => setTip(null),
  });

  return (
    <div className={`app ${isCanvas ? 'app--canvas' : ''} ${isDark ? 'app--dark' : ''}`}>
      {mobileOpen && <div className="rail-overlay" onClick={() => setMobileOpen(false)} />}

      <aside className={`rail ${mobileOpen ? 'open' : ''}`} aria-label="Main">
        <Link to="/dashboard" className="rail-logo" aria-label="CivilFox home">
          <img src={logoImg} alt="" />
        </Link>

        <div className="rail-cds">
          <CountdownChip letter="P" label="Prelims" date={EXAM_DATES.prelims} tipProps={tipProps} />
          <CountdownChip letter="M" label="Mains" date={EXAM_DATES.mains} tipProps={tipProps} />
        </div>

        <span className="rail-sep" aria-hidden="true" />

        <nav className="rail-nav">
          {NAV.map(({ to, Icon, label, external }) => (external ? (
            <a key={to} href={to} target="_blank" rel="noopener noreferrer" className="rail-item"
              aria-label={label} {...tipProps(label)} onClick={() => { setTip(null); setMobileOpen(false); }}>
              <Icon size={20} strokeWidth={2} />
              <span className="rail-label">{label}</span>
            </a>
          ) : (
            <NavLink key={to} to={to} aria-label={label} {...tipProps(label)}
              className={({ isActive }) => `rail-item ${isActive ? 'active' : ''}`}
              onClick={() => { setTip(null); setMobileOpen(false); }}>
              <Icon size={20} strokeWidth={2} />
              <span className="rail-label">{label}</span>
            </NavLink>
          )))}
        </nav>

        <button type="button" className="rail-item rail-out" aria-label="Sign Out" {...tipProps('Sign Out')}
          onClick={() => { setTip(null); logout(); navigate('/login'); }}>
          <LogOut size={20} strokeWidth={2} />
          <span className="rail-label">Sign Out</span>
        </button>
      </aside>

      {tip && <span className="rail-tip" style={{ top: tip.y }} role="presentation">{tip.text}</span>}

      <main className={`app-main main-content ${isBleed ? 'app-main--bleed main-content--full-bleed' : ''}`}>
        {/* Phones only: menu button and logo */}
        <div className="app-bar">
          <button type="button" className="app-bar-btn" onClick={() => setMobileOpen(o => !o)} aria-label="Menu">
            <Menu size={20} />
          </button>
          <Link to="/dashboard" className="app-bar-logo" aria-label="CivilFox home"><img src={logoImg} alt="" /></Link>
        </div>

        <div className={`app-page page-content ${isBleed ? 'page-content--full-bleed' : ''}`}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}